/**
 * resolveSignifyFieldValues
 *
 * The whole Signify mapping contract, in one pure pass. Consumers: the React
 * line-item builders and mapping settings preview, and `buildManualEntry` in
 * the Functions workflow action. The contract: (1) rules are ordered and the
 * FIRST rule whose `when` matches wins, a rule with no `when` matching every
 * response; (2) a condition compares one response value against a set, both
 * sides normalized with `String(value).toUpperCase().trim()`, and an absent,
 * `null` or `undefined` path never matches; (3) conditions read the response
 * only, never another destination's resolved value, so resolution has no order
 * between destinations and no cycles; (4) within the matched rule `sources` is
 * an ordered fallback where a `path` source contributes only a value that is
 * not `null`, not `undefined` and not `''` (`false` and `0` DO contribute) and
 * a `constant` always contributes; (5) a matched rule whose every source is
 * absent yields NO entry and does NOT fall through to a later rule, which
 * preserves "an override replaces the whole list"; (6) resolution is
 * destination-driven, so two destinations reading one source is legitimate
 * rather than a collision. Prices are addressed by path only after
 * `indexSignifyProductPrices` keys them by `priceTypeIdentifier`.
 */

import type {
  SignifyFieldCondition,
  SignifyFieldMappings,
  SignifyValueSource,
} from './SignifyFieldRule.types';

export type ResolvedSignifyField = {
  value: unknown;
  /** The source that supplied the value. */
  source: SignifyValueSource;
  /** Index of the matched rule, so a UI can explain why a field got its value. */
  ruleIndex: number;
};

const isIndexable = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

/**
 * Reads a dot-notation path out of a Signify response, the way rule matching
 * and source fallback read it: every step must be indexable, so a path that
 * continues past a primitive yields `undefined` rather than indexing into it.
 * Public so a surface that re-reads the sources of a rule the engine matched —
 * the product modal's record-name resolver — sees the same values the engine
 * saw.
 */
export const readSignifyResponsePath = (
  response: Record<string, unknown>,
  path: string,
): unknown =>
  path
    .split('.')
    .reduce<unknown>((value, key) => (isIndexable(value) ? value[key] : undefined), response);

const normalizeConditionValue = (value: unknown): string => String(value).toUpperCase().trim();

const conditionMatches = (
  condition: SignifyFieldCondition,
  response: Record<string, unknown>,
): boolean => {
  const value = readSignifyResponsePath(response, condition.path);
  if (value === null || value === undefined) {
    return false;
  }
  const normalized = normalizeConditionValue(value);
  return condition.equals.some((candidate) => normalizeConditionValue(candidate) === normalized);
};

const readFirstUsableSource = (
  sources: SignifyValueSource[],
  response: Record<string, unknown>,
): { source: SignifyValueSource; value: unknown } | undefined => {
  for (const source of sources) {
    if (source.type === 'constant') {
      return { source, value: source.value };
    }
    const value = readSignifyResponsePath(response, source.path);
    if (value !== null && value !== undefined && value !== '') {
      return { source, value };
    }
  }
  return undefined;
};

export const resolveSignifyFieldValues = (params: {
  fieldMappings: SignifyFieldMappings;
  response: Record<string, unknown>;
}): Record<string, ResolvedSignifyField> => {
  const { fieldMappings, response } = params;
  return Object.fromEntries(
    Object.entries(fieldMappings).flatMap<[string, ResolvedSignifyField]>(([fieldId, rules]) => {
      const ruleIndex = rules.findIndex(
        (rule) => rule.when === undefined || conditionMatches(rule.when, response),
      );
      if (ruleIndex === -1) {
        return [];
      }
      const matched = readFirstUsableSource(rules[ruleIndex].sources, response);
      return matched === undefined
        ? []
        : [[fieldId, { value: matched.value, source: matched.source, ruleIndex }]];
    }),
  );
};
